import { useTranslation } from "react-i18next";
import PageWrapper from "../../components/PageWrapper";
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { useContext, useEffect, useState } from "react";
import BackToDashboard from "../../components/BackToDashboard";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
// import saveIcon from "../../assets/save_icon.png";
// import closeIcon from "../../assets/close_icon.png";
// import editIcon from "../../assets/edit_icon.png";
// import addIcon from "../../assets/add_icon_light.png";
// import deleteIcon from "../../assets/delete_Icon.png";
import ErrorBox from "../../components/ErrorBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import axiosClient from "../../services/axiosClient";
import AreYouSureModal from "../../components/AreYouSureModal";
import HotdropdownV2 from "../../components/HotdropdownV2";
import SubscriptionStatusBadge from "../../components/SubscriptionStatusBadge";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";

async function getSubscriptionPlans(params = {}) {
  return axiosClient.get("/subscription/getAll", { params });
}

async function createSubscriptionPlan(data) {
  return axiosClient.post("/subscription", data);
}

async function updateSubscriptionPlan(id, data) {
  return axiosClient.put(`/subscription/${id}`, data);
}

async function deleteSubscriptionPlan(id) {
  return axiosClient.delete(`/subscription/${id}`);
}

const ManageSubscriptionPlans = () => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const tableTitles = [
    {
      value: "planName",
      keepOnMobile: true,
    },
    {
      value: "numberOfUsers",
      keepOnMobile: true,
    },
    {
      value: "price",
      keepOnMobile: true,
    },
    {
      value: "subscribers",
      keepOnMobile: true,
    },
    {
      value: "status",
      keepOnMobile: true,
    },
    {
      value: "Actions",
      keepOnMobile: true,
    },
  ];

  const fetchSubscriptionPlans = async () => {
    setIsLoading(true);
    try {
      const data = await getSubscriptionPlans({
        page: pagination.currentPage,
      });
      setSubscriptionPlans(data?.data?.Subs || []);
      setPagination((prev) => ({
        ...prev,
        totalPages: data?.data?.totalPages || 1,
        totalRecords: data?.data?.totalRecords || 0,
      }));
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const tableRows = subscriptionPlans?.map((plan) => [
    {
      value: plan?.name,
      type: TABLE_CELL_TYPES.TEXT,
      eclipseText: true,
      keepOnMobile: true,
    },
    {
      value: plan?.userNumber ?? 0,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: `${plan?.pricePerMonth ?? 0} US$`,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: plan?.subscribers ?? 0,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: <SubscriptionStatusBadge status={plan?.status} />,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: (
        <CustomButton
          variant={"blank"}
          icon='edit'
          iconSize={18}
          onClick={() => {
            setSelectedPlan({
              id: plan?.id,
              name: plan?.name,
              description: plan?.description,
              pricePerMonth: plan?.pricePerMonth,
              userNumber: plan?.userNumber,
              status: plan?.status,
            });
            setShowEditModal(true);
          }}
          title={t("edit")}
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  useEffect(() => {
    fetchSubscriptionPlans();
  }, [pagination.currentPage]);

  const onCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedPlan(null);
  };

  const onCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleSuccess = () => {
    fetchSubscriptionPlans();
  };

  return (
    <PageWrapper
      style={{ direction: currentDirection }}

      title={t("subscriptionPlans")}
    >
      <div className="o-column o-column--gap-size_500 u-margin--top_500">
        <div className="o-row o-row--horizontal-align_end">
          <CustomButton
            title={t("addNewPlan")}
            icon='add'
            iconSize={16}
            onClick={() => setShowAddModal(true)}
            style={{ marginStart: "auto" }}
          />
        </div>

        <section>
          {isLoading ? (
            <Loader size={LOADER_SIZES.MEDIUM} />
          ) : (
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
            />
          )}
        </section>
      </div>

      {showEditModal && (
        <PlanModal
          open={showEditModal}
          onClose={onCloseEditModal}
          plan={selectedPlan}
          onSuccess={handleSuccess}
          isEdit={true}
        />
      )}

      {showAddModal && (
        <PlanModal
          open={showAddModal}
          onClose={onCloseAddModal}
          plan={null}
          onSuccess={handleSuccess}
          isEdit={false}
        />
      )}
    </PageWrapper>
  );
};

const PlanModal = ({ open, onClose, plan, onSuccess, isEdit }) => {
  const { t } = useTranslation("general");
  const [planData, setPlanData] = useState({
    name: plan?.name || "",
    description: plan?.description || "",
    pricePerMonth: plan?.pricePerMonth || "",
    userNumber: plan?.userNumber || "",
    status: plan?.status || "ACTIVE",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Validation functions
  const validatePlanName = (value) => {
    if (!value || value.trim() === "") {
      return t("planNameRequired");
    }
    if (value.length < 2) {
      return t("planNameMinLength");
    }
    return "";
  };

  const validatePrice = (value) => {
    const numValue = parseFloat(value);
    if (!value || isNaN(numValue)) {
      return t("priceRequired");
    }
    if (numValue < 0) {
      return t("priceMinValue");
    }
    return "";
  };

  const validateUserNumber = (value) => {
    const numValue = parseInt(value);
    if (!value || isNaN(numValue)) {
      return t("userNumberRequired");
    }
    if (numValue < 1) {
      return t("userNumberMinValue");
    }
    return "";
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setPlanData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    // Validate plan name
    const planNameError = validatePlanName(planData.name);
    if (planNameError) newErrors.name = planNameError;

    // Validate price
    const priceError = validatePrice(planData.pricePerMonth);
    if (priceError) newErrors.pricePerMonth = priceError;

    // Validate user number
    const userNumberError = validateUserNumber(planData.userNumber);
    if (userNumberError) newErrors.userNumber = userNumberError;

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      setErrors({});

      const submitData = {
        name: planData.name,
        description: planData.description,
        price: parseFloat(planData.pricePerMonth),
        usersNumber: parseInt(planData.userNumber),
        status: planData.status,
      };

      if (isEdit && plan?.id) {
        await updateSubscriptionPlan(plan.id, submitData);
      } else {
        await createSubscriptionPlan(submitData);
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving subscription plan:", error);
      setErrors({ submit: t("saveError") });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formError =
    Object.keys(errors).length > 0
      ? errors.submit
        ? errors.submit
        : t("inputsCheckError")
      : null;

  async function handleDeleteSubscriptionPlan(id) {
    try {
      await deleteSubscriptionPlan(id);
      setShowDeleteConfirmation(false);
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Error deleting subscription plan:", error);
    }
  }

  return (
    <Modal
      title={isEdit ? "editSubscriptionPlan" : "addNewSubscriptionPlan"}
      onClose={onClose}
      open={open}
      error={formError}
      footer={
        <>
          {isSubmitting && <Loader size={LOADER_SIZES.SMALL} />}

          <section className="edit-user-actions">
            <CustomButton
              title={isSubmitting ? t("saving") : t("confirm")}
              icon='save'
              iconSize={16}
              onClick={handleSubmit}
              disabled={isSubmitting}
            />
            <CustomButton
              type="button"
              title={t("cancel")}
              variant="blank"
              icon="close"
              iconSize={14}
              onClick={onClose}
              disabled={isSubmitting}
            />
            {isEdit && (
              <CustomButton
                type="button"
                title={t("delete")}
                variant="red"
                icon='delete'
                iconSize={14}
                onClick={() => setShowDeleteConfirmation(true)}
                disabled={isSubmitting}
              />
            )}
          </section>
        </>
      }
    >
      {showDeleteConfirmation && (
        <AreYouSureModal
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={() => handleDeleteSubscriptionPlan(plan.id)}
          customMessage={t("deleteSubscriptionPlanDescription")}
          closeTitle="cancel"
          confirmTitle="confirm"
          confirmIconType="whiteCheck"
          cancel
        />
      )}
      <section>
        {formError && <ErrorBox>{formError}</ErrorBox>}

        <form onSubmit={handleSubmit}>
          <div className="o-column o-column--gap-size_500">
            {/* Plan Name */}
            <div className="o-column o-column--gap-size_200">
              <span>{t("planName")}: </span>
              {errors.name && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.name}
                </span>
              )}
              <CustomInput
                type="text"
                value={planData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                style={{ borderColor: errors.name ? "red" : undefined }}
                placeholder={t("enterPlanName")}
              />
            </div>

            {/* Description */}
            <div className="o-column o-column--gap-size_200">
              <span>{t("description")}: </span>
              <CustomInput
                type="textarea"
                rows={3}
                value={planData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder={t("enterPlanDescription")}
              />
            </div>

            {/* Price Per Month */}
            <div className="o-column o-column--gap-size_200">
              <span>{t("pricePerMonth")}: </span>
              {errors.pricePerMonth && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.pricePerMonth}
                </span>
              )}
              <CustomInputWithUnit
                type="number"
                min="0"
                value={planData.pricePerMonth}
                onChange={(e) =>
                  handleInputChange("pricePerMonth", e.target.value)
                }
                style={{
                  borderColor: errors.pricePerMonth ? "red" : undefined,
                }}
                placeholder={t("enterPricePerMonth")}
                unit="US$"
              />
            </div>

            {/* User Number */}
            <div className="o-column o-column--gap-size_200">
              <span>{t("userNumber")}: </span>
              {errors.userNumber && (
                <span
                  style={{
                    color: "#cb5b52",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.userNumber}
                </span>
              )}
              <CustomInput
                type="number"
                min="1"
                value={planData.userNumber}
                onChange={(e) =>
                  handleInputChange("userNumber", e.target.value)
                }
                style={{ borderColor: errors.userNumber ? "red" : undefined }}
                placeholder={t("enterUserNumber")}
              />
            </div>

            {/* Status */}
            <div className="o-column o-column--gap-size_200">
              <span>{t("status")}: </span>
              <StatusSelector
                value={planData.status}
                onChange={(value) => handleInputChange("status", value)}
              />
            </div>
          </div>
        </form>
      </section>
    </Modal>
  );
};

function StatusSelector({ value, onChange }) {
  const { t } = useTranslation("general");

  const statusOptions = [
    {
      id: "ACTIVE",
      title: t("active"),
    },
    {
      id: "INACTIVE",
      title: t("inactive"),
    },
  ];

  const handleStatusSelect = (status) => {
    onChange?.(status.id);
  };

  const selected = statusOptions.find((option) => option.id === value);

  return (
    <HotdropdownV2
      selected={selected}
      setSelected={handleStatusSelect}
      initOptions={statusOptions}
      thick
    />
  );
}

export default ManageSubscriptionPlans;
